import { ProcessingSpinner } from "@storytell/chat-ui/ui";
import type { IconTypes } from "solid-icons";
import { TbFileSearch, TbInfoCircle, TbMessageQuestion } from "solid-icons/tb";
import { type Component, For, type JSX, type ParentComponent, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { ScaledFrame } from "../ScaledFrame";
import {
  CHIEF_ACCENT,
  ChiefPane,
  PANE_ROW,
  PaneSection,
  PeopleStack,
  SessionPill,
  SessionWindow,
  startedLabel,
  Swap,
  type TranscriptEntry,
  TranscriptBlock,
} from "./chief";
import { AnswerNotchCard, NotchLive } from "./notch";
import { at, Crown, MockScene, type Person } from "./parts";

type Insight = { time: string; title: string; body: string };

export type InsightsData = {
  project: string;
  session: string;
  started: string;
  clock: string;
  people: Person[];
  /** The question nobody can settle, the reply that can't, then the line that half-remembers a doc. */
  transcript: [TranscriptEntry, TranscriptEntry, TranscriptEntry];
  asked: string;
  lookingAnswer: string;
  lookingFound: string;
  answer: Insight;
  found: Insight;
  /** A card from earlier in the call, already folded to one line. */
  earlier: { time: string; title: string };
};

type SceneProps = { data: InsightsData; label: string };

/**
 * A row arriving in a list or a stack: it opens its own slot so what sits under
 * it moves rather than being covered, and with `leaves`, closes the slot again
 * when what it was waiting for lands.
 */
const Arrives: ParentComponent<{ at: number; leaves?: number }> = (props) => {
  const slot = (content: JSX.Element) => (
    <div class="grow-in grid grid-rows-[1fr]" style={at(props.at)}>
      <div class="min-h-0 overflow-hidden">
        <div class="row-slide" style={at(props.at)}>
          {content}
        </div>
      </div>
    </div>
  );
  return (
    <Show when={props.leaves} fallback={slot(props.children)}>
      {(leaves) => (
        <div class="collapse-out grid grid-rows-[0fr] opacity-0" style={at(leaves())}>
          <div class="min-h-0 overflow-hidden">{slot(props.children)}</div>
        </div>
      )}
    </Show>
  );
};

/** What was just said, lit from the moment Chief takes it up: the cause of the card that follows. */
const Heard: ParentComponent<{ at: number; class: string }> = (props) => (
  <div class="relative">
    <span class={`mark-in pointer-events-none absolute rounded-md ${props.class}`} style={at(props.at)} />
    <div class="relative">{props.children}</div>
  </div>
);

// Seconds into the call scene.
const CALL = {
  question: 0.3,
  heard: 1.1,
  reply: 1.8,
  card: 3.0,
};

/**
 * Chief on the call itself: the meeting app with its captions, and the notch
 * over it. Raj's question goes unanswered, Chief takes it up, and the answer
 * drops out of the notch without anyone switching windows.
 */
export const InsightsCall: Component<SceneProps> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  const question = () => props.data.transcript[0];
  const reply = () => props.data.transcript[1];
  const speakers = [
    { person: props.data.people[1], speaksAt: CALL.question },
    { person: props.data.people[0], speaksAt: CALL.reply },
    { person: props.data.people[2] },
  ];
  return (
    <MockScene scene={scene} label={props.label}>
      <ScaledFrame chrome="app" width={960} height={600} narrow={{ width: 400, height: 760 }}>
        <div class="mk-app relative flex h-full flex-col bg-mock-window text-white">
          <div class="flex h-9 shrink-0 items-center gap-2 px-4">
            <span class="size-3 rounded-full bg-mock-close" />
            <span class="size-3 rounded-full bg-mock-minimize" />
            <span class="size-3 rounded-full bg-mock-zoom" />
            <span class="ms-3 truncate text-xs text-white/60">{props.data.session}</span>
            <BrandGlyph src="/images/logos/zoom.svg" class="ms-auto h-4 w-12 text-white/70" />
          </div>
          <div class="grid grid-cols-2 gap-2 px-3 @2xl:grid-cols-3">
            <For each={speakers}>
              {(speaker) => (
                <div class="relative grid h-32 place-items-center rounded-lg bg-mock-video @2xl:h-44">
                  <span
                    class="grid size-14 place-items-center rounded-full text-xl font-semibold"
                    style={{ "background-color": speaker.person.color }}
                  >
                    {speaker.person.name[0]}
                  </span>
                  <span class="absolute inset-s-2 bottom-1.5 rounded-sm bg-black/50 px-1.5 text-xs text-white/90">
                    {speaker.person.name}
                  </span>
                  <Show when={speaker.speaksAt}>
                    {(speaksAt) => (
                      <span
                        class="blip absolute inset-0 rounded-lg inset-ring-2 inset-ring-mock-speaking"
                        style={at(speaksAt())}
                      />
                    )}
                  </Show>
                </div>
              )}
            </For>
          </div>
          <div class="mx-auto mt-4 flex w-full max-w-xl flex-col gap-2 px-4">
            <Heard at={CALL.heard} class="-inset-1 bg-speaker-them-fill/40 ring-2 ring-speaker-them">
              <Caption entry={question()} at={CALL.question} heardAt={CALL.heard} />
            </Heard>
            <Caption entry={reply()} at={CALL.reply} />
          </div>
          <div class="absolute inset-x-0 top-0 flex flex-col items-center px-3">
            <NotchLive clock={props.data.clock} />
            <Arrives at={CALL.heard} leaves={CALL.card}>
              <div class="flex items-center gap-2 rounded-b-xl bg-black px-4 py-2 text-xs text-white/70">
                <ProcessingSpinner class="size-3 border-notch-accent border-t-transparent" />
                <span class="truncate">{props.data.lookingAnswer}</span>
              </div>
            </Arrives>
          </div>
          <div class="absolute inset-x-0 top-0 flex justify-center px-3">
            <AnswerNotchCard
              class="card-appear w-full max-w-96"
              style={at(CALL.card)}
              eyebrow={
                <>
                  <TbMessageQuestion class="size-3.5" aria-hidden="true" />
                  {t("marketing.mock.insights.you_asked_short")}
                </>
              }
              quote={props.data.asked}
              title={props.data.answer.title}
              body={props.data.answer.body}
            />
          </div>
        </div>
      </ScaledFrame>
    </MockScene>
  );
};

/** One line of the meeting app's live captions; `heardAt` marks it with Chief's crown as Chief takes it up. */
const Caption: Component<{ entry: TranscriptEntry; at: number; heardAt?: number }> = (props) => (
  <p
    class="msg-in flex items-start gap-2 rounded-md bg-black/60 px-3 py-1.5 text-sm leading-snug text-white"
    style={at(props.at)}
  >
    <span class="min-w-0 flex-1">
      <span class="font-semibold text-white/70">{props.entry.speaker}: </span>
      {props.entry.text}
    </span>
    <Show when={props.heardAt}>
      {(heardAt) => (
        <span class="pop-in mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-white" style={at(heardAt())}>
          <Crown class="w-3" />
        </span>
      )}
    </Show>
  </p>
);

// Seconds into the app scene.
const APP = {
  question: 0.3,
  lookAnswer: 0.9,
  reply: 1.5,
  answer: 2.4,
  recall: 3.1,
  lookFound: 3.6,
  found: 4.8,
};

// Each question and the card that answers it share one tint and edge rule, so a
// pair reads across the two columns even in a still frame.
const PAIR = {
  answer: "rounded-md border-s-[3px] border-brand bg-brand/10",
  found: "rounded-md border-s-[3px] border-amber-600 bg-amber-500/10",
};

/**
 * Chief in its own window: the transcript fills in as the call goes, and each
 * line Chief takes up lights as its look starts under Intelligence cards in
 * Activity, and its card lands in the same tint. Nobody asks Chief for either.
 */
export const InsightsApp: Component<SceneProps> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  const question = () => props.data.transcript[0];
  const reply = () => props.data.transcript[1];
  const recall = () => props.data.transcript[2];
  return (
    <MockScene scene={scene} label={props.label}>
      <ScaledFrame chrome="app" width={960} height={600} narrow={{ width: 400, height: 860 }}>
        <SessionWindow
          project={props.data.project}
          title={props.data.session}
          facts={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
          live
          tab="transcript"
          bar={
            <SessionPill clock={props.data.clock} people={<PeopleStack people={props.data.people} />} wave={false} />
          }
          pane={
            <ChiefPane class="h-110 shrink-0 @2xl:h-auto @2xl:w-85">
              <ActivityPanel data={props.data} />
            </ChiefPane>
          }
        >
          <div class="px-5 py-4">
            <Heard at={APP.lookAnswer} class={`-inset-x-1.5 -inset-y-1 ${PAIR.answer}`}>
              <TranscriptBlock entry={{ ...question(), appearAt: APP.question }} />
            </Heard>
            <AnswerMarker icon={TbMessageQuestion} title={props.data.answer.title} appearAt={APP.answer} />
            <TranscriptBlock entry={{ ...reply(), appearAt: APP.reply }} />
            <Heard at={APP.lookFound} class={`-inset-x-1.5 -inset-y-1 ${PAIR.found}`}>
              <TranscriptBlock entry={{ ...recall(), appearAt: APP.recall }} />
            </Heard>
            <AnswerMarker icon={TbFileSearch} title={props.data.found.title} appearAt={APP.found} />
          </div>
        </SessionWindow>
      </ScaledFrame>
    </MockScene>
  );
};

/** The transcript's one-line link under a turn to the card that answers it. */
const AnswerMarker: Component<{ icon: IconTypes; title: string; appearAt: number }> = (props) => (
  <span
    class="mark-in -mt-2 mb-3 flex max-w-full items-center gap-1.5 text-xs text-muted-text"
    style={at(props.appearAt)}
  >
    <Dynamic component={props.icon} class="size-3.5 shrink-0 text-brand" aria-hidden="true" />
    <span class="truncate">{props.title}</span>
  </span>
);

const ActivityPanel: Component<{ data: InsightsData }> = (props) => {
  const { t } = useI18n();
  return (
    <div class="flex flex-col p-1.5">
      {/* One card was up before the scene starts; each that lands adds one. */}
      <PaneSection
        verb="found"
        count={<Swap before="1" after={<Swap before="2" after="3" swapAt={APP.found} />} swapAt={APP.answer} />}
      />
      <Arrives at={APP.found}>
        <IntelRow
          icon={TbFileSearch}
          kind={t("marketing.mock.insights.found")}
          title={props.data.found.title}
          time={props.data.found.time}
          class={PAIR.found}
        />
      </Arrives>
      <Arrives at={APP.lookFound} leaves={APP.found}>
        <LookingCard icon={TbFileSearch} text={props.data.lookingFound} />
      </Arrives>
      <Arrives at={APP.answer}>
        <IntelRow
          icon={TbMessageQuestion}
          kind={t("marketing.mock.insights.answer")}
          title={props.data.answer.title}
          time={props.data.answer.time}
          class={PAIR.answer}
        />
      </Arrives>
      <Arrives at={APP.lookAnswer} leaves={APP.answer}>
        <LookingCard icon={TbMessageQuestion} text={props.data.lookingAnswer} />
      </Arrives>
      <IntelRow
        icon={TbInfoCircle}
        kind={t("marketing.mock.insights.context")}
        title={props.data.earlier.title}
        time={props.data.earlier.time}
      />
    </div>
  );
};

/** A card Chief is still writing: what it is looking into, and the lines it will fill. */
const LookingCard: Component<{ icon: IconTypes; text: string }> = (props) => (
  <div class="my-1 flex flex-col gap-2 rounded-md bg-tile p-2.5 ring-1 ring-primary-border">
    <div class="flex items-start gap-2">
      <span class="grid size-5.5 shrink-0 place-items-center rounded-md bg-panel-hover">
        <Dynamic component={props.icon} class={`size-3.5 ${CHIEF_ACCENT}`} aria-hidden="true" />
      </span>
      <span class="min-w-0 pt-0.5 text-sm leading-snug text-primary-text">{props.text}</span>
    </div>
    <div class="flex flex-col gap-1.5">
      <span class="h-2 w-[90%] rounded-full bg-panel-hover" />
      <span class="h-2 w-full rounded-full bg-panel-hover" />
      <span class="h-2 w-[55%] rounded-full bg-panel-hover" />
    </div>
  </div>
);

/** One Intelligence card, folded to its line: its mark, the kind as a word, the title, the time. */
const IntelRow: Component<{ icon: IconTypes; kind: string; title: string; time: string; class?: string }> = (props) => (
  <div class={`${PANE_ROW} ${props.class ?? ""}`}>
    <Dynamic component={props.icon} class={`size-3.5 ${CHIEF_ACCENT}`} aria-hidden="true" />
    <span class="flex min-w-0 items-center gap-2">
      <span class={`shrink-0 text-xs font-semibold ${CHIEF_ACCENT}`}>{props.kind}</span>
      <span class="truncate text-primary-text">{props.title}</span>
    </span>
    <span class="text-xs text-muted-text tabular-nums">{props.time}</span>
  </div>
);
